import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import LessonDetails from '../../components/Lesson/LessonDetails';
import Breadcrumb from '../../components/Breadcrumb';
import { getLesson } from '../../services/lessonService';
import { Lesson } from '../../types/interfaces';
import { Container, CircularProgress, Typography, Button, Box, Tabs, Tab, Paper } from '@mui/material';
import ExerciseList from '../../components/Exercise/ExerciseList';
import AddIcon from '@mui/icons-material/Add';
import LearningMaterialList from '../../components/LearningMaterial/LearningMaterialList';
import AuthoringAssistantPanel from '../../components/AI/AuthoringAssistantPanel';
import { useAuth } from '../../components/AuthContext';
import { UserRole } from '../../common/enums';
import { buildAuthoringAssistantCreateState } from '../../components/AI/authoringAssistantNavigation';

const LessonViewPage: React.FC = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();
  const canUseAuthoringAssistant = user?.role === UserRole.Admin || user?.role === UserRole.Tutor;

  useEffect(() => {
    const fetchData = async () => {
      if (courseId && lessonId) {
        const response = await getLesson(parseInt(courseId, 10), parseInt(lessonId, 10));
        setLesson(response.data);
      }
      setLoading(false);
    };

    fetchData();
  }, [courseId, lessonId]);

  if (loading) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  if (!lesson) {
    return (
      <Container>
        <Typography variant="h5" component="div">
          Oppituntia ei löytynyt
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          flexDirection: { xs: 'column', xl: 'row' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {courseId && lessonId && <Breadcrumb courseId={courseId} lessonId={lessonId} />}

          <Box mb={3}>
            <LessonDetails lesson={lesson} />
          </Box>

          <Paper sx={{ mb: 3 }}>
            <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} variant="fullWidth">
              <Tab label="Harjoitukset" />
              <Tab label="Materiaalit" />
            </Tabs>
          </Paper>

          {tabValue === 0 && (
            <>
              <Box mb={4}>
                {lessonId && (
                  <Button
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to={`/course/${courseId}/lesson/${lessonId}/exercise/create`}
                    startIcon={<AddIcon />}
                  >
                    Lisää uusi harjoitus
                  </Button>
                )}
              </Box>

              <Box mb={6}>
                {lessonId && courseId && <ExerciseList lessonId={lessonId} courseId={courseId} />}
              </Box>
            </>
          )}

          {tabValue === 1 && (
            <Box mb={6}>
              {lessonId && <LearningMaterialList lessonId={parseInt(lessonId, 10)} isEditable={true} />}
            </Box>
          )}
        </Box>

        {canUseAuthoringAssistant && courseId && lessonId && (
          <AuthoringAssistantPanel
            courseId={parseInt(courseId, 10)}
            lessonId={parseInt(lessonId, 10)}
            draftLesson={{
              title: lesson.title,
              description: lesson.description,
              order: lesson.order,
              isActive: lesson.isActive,
            }}
            createActions={[
              {
                id: 'create-material',
                label: 'Aloita uusi materiaali',
                onCreate: (prompt) => {
                  navigate(`/course/${courseId}/lesson/${lessonId}/material/create`, {
                    state: buildAuthoringAssistantCreateState('material', prompt),
                  });
                },
              },
              {
                id: 'create-exercise',
                label: 'Aloita uusi harjoitus',
                onCreate: (prompt) => {
                  navigate(`/course/${courseId}/lesson/${lessonId}/exercise/create`, {
                    state: buildAuthoringAssistantCreateState('exercise', prompt),
                  });
                },
              },
            ]}
          />
        )}
      </Box>
    </Container>
  );
};

export default LessonViewPage;
